/**
 * Marks legend terms in rendered markdown: a rehype plugin that wraps the first
 * occurrence of each term in a `<span data-term-index>` for `TermLegend` to
 * render.  Pure, so it can be tested without React.
 */
export interface MarkableTerm {
  term: string;
}

// ── Marking terms in the rendered markdown ───────────────────────────────────

export const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}_]/u.test(c);

/** First occurrence of `term` in `s` that isn't the middle of a longer word. */
export function findTerm(s: string, term: string): number {
  let from = 0;
  for (;;) {
    const at = s.indexOf(term, from);
    if (at < 0) return -1;
    const startsWord = !isWordChar(term[0]) || !isWordChar(s[at - 1]);
    const endsWord = !isWordChar(term[term.length - 1]) || !isWordChar(s[at + term.length]);
    if (startsWord && endsWord) return at;
    from = at + 1;
  }
}

/**
 * Rehype plugin: wrap the first occurrence of each term in a
 * `<span data-term-index>`.  Code blocks and links are left alone.
 */
export function rehypeTerms(terms: MarkableTerm[]) {
  return () => (tree: any) => {
    if (terms.length === 0) return;
    const remaining = new Map(terms.map((t, i) => [t.term, i]));
    const visit = (parent: any) => {
      const kids = parent.children;
      if (!Array.isArray(kids)) return;
      for (let i = 0; i < kids.length && remaining.size > 0; i++) {
        const node = kids[i];
        if (node.type === 'element') {
          if (node.tagName !== 'pre' && node.tagName !== 'a') visit(node);
          continue;
        }
        if (node.type !== 'text') continue;
        let best: { at: number; term: string; idx: number } | null = null;
        for (const [term, idx] of remaining) {
          const at = findTerm(node.value, term);
          if (at >= 0 && (!best || at < best.at || (at === best.at && term.length > best.term.length))) best = { at, term, idx };
        }
        if (!best) continue;
        const before = node.value.slice(0, best.at);
        const after = node.value.slice(best.at + best.term.length);
        const replacement: any[] = [];
        if (before) replacement.push({ type: 'text', value: before });
        replacement.push({
          type: 'element',
          tagName: 'span',
          properties: { dataTermIndex: String(best.idx) },
          children: [{ type: 'text', value: best.term }],
        });
        if (after) replacement.push({ type: 'text', value: after });
        kids.splice(i, 1, ...replacement);
        remaining.delete(best.term);
        // Continue with the text after the term, if any.
        i += replacement.length - (after ? 2 : 1);
      }
    };
    visit(tree);
  };
}

